import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** User preference: light, dark, or follow system. */
export type ThemePreference = 'light' | 'dark' | 'system'

/** Resolved theme after applying system preference. */
export type ResolvedTheme = 'light' | 'dark'

interface ThemeState {
  /** User's theme preference. */
  preference: ThemePreference
  /** Set the theme preference. */
  setPreference: (preference: ThemePreference) => void
  /** Toggle between light and dark (skips system). */
  toggleTheme: () => void
}

export const THEME_STORE_KEY = 'stream-theme'

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      preference: 'dark',
      setPreference: (preference) => {
        set({ preference })
        applyThemePreference(preference)
      },
      toggleTheme: () => {
        const current = get().preference
        // If system, check what it resolved to and toggle from there
        const resolved = current === 'system' ? getSystemPreference() : current
        const newPreference: ThemePreference = resolved === 'dark' ? 'light' : 'dark'
        set({ preference: newPreference })
        applyThemePreference(newPreference)
      },
    }),
    {
      name: THEME_STORE_KEY,
      onRehydrateStorage: () => (state) => {
        if (state?.preference) {
          applyThemePreference(state.preference)
        }
      },
    }
  )
)

/**
 * Resolve the concrete theme given a preference and system-preference state.
 */
export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light'
  return preference
}

/**
 * Get the system's color scheme preference.
 */
function getSystemPreference(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Apply a theme preference to the document.
 */
function applyThemePreference(preference: ThemePreference) {
  if (typeof document === 'undefined') return
  const resolved = preference === 'system' ? getSystemPreference() : preference
  document.documentElement.setAttribute('data-theme', resolved)
}

/**
 * Set up listener for system theme changes when preference is 'system'.
 */
function setupSystemThemeListener() {
  if (typeof window === 'undefined') return

  const media = window.matchMedia('(prefers-color-scheme: dark)')

  const handleChange = () => {
    const state = useThemeStore.getState()
    if (state.preference === 'system') {
      applyThemePreference('system')
    }
  }

  media.addEventListener('change', handleChange)
}

// Initialize theme on load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem(THEME_STORE_KEY)
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      if (parsed.state?.preference) {
        applyThemePreference(parsed.state.preference)
      }
    } catch {
      // Default to dark
      applyThemePreference('dark')
    }
  } else {
    applyThemePreference('dark')
  }

  // Listen for system theme changes
  setupSystemThemeListener()
}
